import { memo } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useWeatherStore } from '../../store/weatherStore';
import { useTranslation } from '../../i18n/LanguageContext';
import { BASE_SCALAR_LAYER_IDS, type ForecastGridType } from '../../config/weatherLayers';
import { getHeatmapColor } from '../../services/colorScales';
import type { LayerKey } from '../../store/types';
import { radarGradientCss, RADAR_LEGEND_LABELS, RADAR_UNIT } from '../../config/radarPalette';

/**
 * ارتفاع شريط المفتاح على الجوّال بالبكسل (‏top-0 inset-x-0، صفّ أرقام بحجم 13px
 * وحشو 5px أعلى وأسفل). ليس رقماً تزيينياً: العناصر التي تقف تحته (الشعار ومبدّل
 * النموذج) تُزاح بمقداره بالضبط حين يغيب، فتحتفظ بهامشها من حافّة الشاشة كما هو.
 */
export const MOBILE_LEGEND_STRIP_H = 30;

/**
 * الطبقات التي لها مفتاح ألوان — **مصدر الحقيقة الوحيد** لسؤال «هل يظهر المفتاح؟».
 *
 * لماذا ثابت منفصل بدل تكرار الشرط عند كل مستهلك؟ لأن موضع الشعار ومبدّل النموذج
 * صار **دالّة في وجود المفتاح**. ولو حكم كلٌّ منهما بنسخته الخاصة من الشرط، لكان
 * أوّل تغيير في قائمة المفاتيح يُنتج واجهة متناقضة مع نفسها بلا خطأ يُنبّه: شريط
 * ظاهر وشعارٌ يعتقد أنه غائب فيرتفع فوقه.
 *
 * والقائمة تُطابَق آلياً مع كائن `legends` المبني داخل المكوّن (فحص التطوير أدناه)،
 * فإضافة مفتاح جديد بلا تحديث هنا تُصرخ في الكونسول بدل أن تختلّ بصمت.
 */
const LEGEND_LAYER_IDS: ReadonlySet<string> = new Set<string>([
    'wind', 'wind-gusts', 'temperature', 'feels-like', 'wet-bulb', 'k-index', 'o-index',
    'precipitation', 'pressure', 'humidity', 'dewpoint', 'clouds', 't850-anomaly',
    'sst-anomaly-rel', 'sst-anomaly',
    // الرادار ليس طبقةً سلَّمية من النموذج (فليس في BASE_SCALAR_LAYER_IDS) لكنه يملأ
    // الخريطة لوناً ويحتاج مفتاحاً كغيره — راجع legendLayerFor أدناه.
    'radar',
    'radar-sim',
]);

/**
 * أي طبقة يُعرض مفتاحها الآن؟ **الدالّة الوحيدة التي تقرّر**، ويشتقّ منها كلٌّ من
 * المكوّن نفسه و`hasCentralLegend` (وهي بدورها تحكم موضع الشعار ومبدّل النموذج).
 *
 * الرادار أوّلاً لأنه طبقة **قاعدية** تُطفئ الطبقات السلَّمية عند تشغيلها؛ فإن كان
 * فعّالاً فهو ما يراه المستخدم ملوّناً، ومفتاحه هو المطلوب.
 */
function legendLayerFor(visibleLayers: Record<LayerKey, boolean>): string | null {
    if (visibleLayers.radar) return 'radar';
    const active = BASE_SCALAR_LAYER_IDS.find((layer) => visibleLayers[layer]);
    return active !== undefined && LEGEND_LAYER_IDS.has(active) ? active : null;
}

/**
 * هل يُرسم مفتاح الألوان للحالة الحالية؟ يجيب بنفس منطق `CentralLegend` حرفياً:
 * لا مفتاح ما لم تكن هناك طبقة سلَّمية قاعدية فعّالة **ولها** تعريف مفتاح.
 *
 * الحالة العملية اليوم: القمر الصناعي والرادار طبقتان قاعديتان تُطفئان الطبقات
 * السلَّمية، فلا تترك أيّاً منها فعّالة ⇒ لا مفتاح ⇒ يفرغ أعلى الشاشة على الجوّال.
 */
export function hasCentralLegend(visibleLayers: Record<LayerKey, boolean>): boolean {
    return legendLayerFor(visibleLayers) !== null;
}

interface LegendSpec {
    title: string;
    gradient: string;
    labels: string[];
}

/**
 * يولّد تدرّج الشريط من getHeatmapColor نفسه (مصدر الحقيقة الوحيد للألوان) —
 * فيطابق الشريط ألوان الخريطة المرسومة بكسلاً ببكسل، بدل تدرّج CSS يدوي منفصل.
 * transform اختياري لمحاور غير خطّية (مثل محور الأمطار الجذر-تكعيبي كما في Zoom Earth).
 */
function gradientFor(
    type: ForecastGridType,
    min: number,
    max: number,
    transform?: (t: number) => number,
    steps = 48,
): string {
    const stops: string[] = [];
    for (let i = 0; i <= steps; i++) {
        const t = i / steps;
        const v = min + (max - min) * (transform ? transform(t) : t);
        const color = getHeatmapColor(v, type) ?? 'rgba(255,255,255,0)';
        stops.push(`${color} ${(t * 100).toFixed(1)}%`);
    }
    return `linear-gradient(to right, ${stops.join(', ')})`;
}

/**
 * تدرّج بمحور غير خطّي — كل نقطة تسمية (breakpoint) تأخذ نفس المساحة على الشريط
 * بصرياً بغضّ النظر عن قيمتها الفعلية، تماماً كشريط الرياح في Windy.com (0/10/20/35/55/70/100
 * بمسافات متساوية). الألوان داخل كل قطعة تبقى الاستيفاء الحقيقي من مقياس الألوان
 * (getHeatmapColor) بين قيمتَي القطعة، فلا يُفقَد أي تفصيل لوني — فقط توزيع المحور يتغيّر.
 */
function gradientForBreakpoints(
    type: ForecastGridType,
    breakpoints: number[],
    stepsPerSegment = 12,
): string {
    const stops: string[] = [];
    const segments = breakpoints.length - 1;
    for (let seg = 0; seg < segments; seg += 1) {
        const v0 = breakpoints[seg];
        const v1 = breakpoints[seg + 1];
        const startI = seg === 0 ? 0 : 1;
        for (let i = startI; i <= stepsPerSegment; i += 1) {
            const t = i / stepsPerSegment;
            const v = v0 + (v1 - v0) * t;
            const pos = ((seg + t) / segments) * 100;
            const color = getHeatmapColor(v, type) ?? 'rgba(255,255,255,0)';
            stops.push(`${color} ${pos.toFixed(2)}%`);
        }
    }
    return `linear-gradient(to right, ${stops.join(', ')})`;
}

// نقاط تسمية الرياح — تطابق تباعد شريط Windy.com تماماً (0/10/20/35/55/70/100).
const WIND_BREAKPOINTS = [0, 10, 20, 35, 55, 70, 100];

// نقاط تسمية الأمطار — تطابق مفتاح ألوان Windy.com حرفياً حتى 30 مم/س (1.5/2/3/7/10/20/30)،
// ثم 40/50 امتداد إضافي حتى لا يتوقّف المفتاح عند 30 بينما الخريطة الفعلية تستمرّ بألوان
// أعمق حتى 50 (راجع PRECIP_HEATMAP_STOPS في colorScales.ts للألوان الفعلية).
// 0 إضافية في البداية لتمثيل نطاق "شبه شفاف" قبل أول علامة Windy، بنفس أسلوب WIND_BREAKPOINTS.
const PRECIP_BREAKPOINTS = [0, 1.5, 2, 3, 7, 10, 20, 30, 40, 50];

// بلا props — memo يمنع إعادة الرسم مع كل تغيّر إطار زمني في NewWeatherMap.
export const CentralLegend = memo(function CentralLegend() {
    const { t } = useTranslation();
    const {
        visibleLayers,
        units,
    } = useWeatherStore(useShallow((s) => ({ visibleLayers: s.visibleLayers, units: s.units })));
    const activeLayer = legendLayerFor(visibleLayers);
    if (!activeLayer) return null;

    // رمز الوحدة يدخل العنوان عبر {unit}؛ يُقرأ منه لاحقاً لعرضه بجانب الأرقام.
    const tempUnit = t(units.temperature === 'celsius' ? 'unit.celsius' : 'unit.fahrenheit');

    // المدى والأرقام مطابقة لشريط Zoom Earth لكل طبقة (الحرارة −30..50 لا −50..50).
    const legends: Record<string, LegendSpec> = {
        wind: {
            title: t('legend.wind', { unit: t('unit.kmh') }),
            gradient: gradientForBreakpoints('wind', WIND_BREAKPOINTS),
            labels: WIND_BREAKPOINTS.map(String),
        },
        'wind-gusts': {
            title: t('legend.wind-gusts', { unit: t('unit.kmh') }),
            gradient: gradientForBreakpoints('wind-gusts', WIND_BREAKPOINTS),
            labels: WIND_BREAKPOINTS.map(String),
        },
        temperature: {
            title: t('legend.temperature', { unit: tempUnit }),
            gradient: gradientFor('temperature', -30, 50),
            labels: units.temperature === 'celsius'
                ? ['-30', '-20', '-10', '0', '10', '20', '30', '40', '50']
                : ['-22', '-4', '14', '32', '50', '68', '86', '104', '122'],
        },
        'feels-like': {
            title: t('legend.feels-like', { unit: tempUnit }),
            gradient: gradientFor('feels-like', -30, 50),
            labels: units.temperature === 'celsius'
                ? ['-30', '-20', '-10', '0', '10', '20', '30', '40', '50']
                : ['-22', '-4', '14', '32', '50', '68', '86', '104', '122'],
        },
        'wet-bulb': {
            title: t('legend.wet-bulb', { unit: tempUnit }),
            gradient: gradientFor('wet-bulb', 0, 40),
            labels: units.temperature === 'celsius'
                ? ['0', '5', '10', '15', '20', '25', '30', '35', '40']
                : ['32', '41', '50', '59', '68', '77', '86', '95', '104'],
        },
        'k-index': {
            title: t('legend.k-index'),
            gradient: gradientFor('k-index', 15, 45),
            labels: ['15', '20', '25', '30', '35', '40', '45'],
        },
        'o-index': {
            title: t('legend.o-index'),
            gradient: gradientFor('o-index', 0, 5000),
            labels: ['0', '500', '1000', '1750', '2500', '4000', '5000'],
        },
        precipitation: {
            // اللون الآن مبني على تراكم 3 ساعات لا معدّل لحظي (راجع getAccumulatedFrameUrl
            // في rasterSampler.ts) — العنوان يعكس ذلك كي لا يقرأ المستخدم "مم/ساعة" فوق لون
            // يمثّل تراكماً فعلياً.
            title: t('legend.precipitation', { unit: t(units.precipitation === 'mm' ? 'unit.mm' : 'unit.inch') }),
            gradient: gradientForBreakpoints('precipitation', PRECIP_BREAKPOINTS),
            labels: units.precipitation === 'mm'
                ? PRECIP_BREAKPOINTS.map(String)
                : PRECIP_BREAKPOINTS.map((v) => (v / 25.4).toFixed(2)),
        },
        pressure: {
            title: t('legend.pressure', { unit: t(units.pressure === 'hPa' ? 'unit.hpa' : 'unit.inhg') }),
            // مدى الشريط وعلاماته من مفتاح Zoom Earth حرفياً. وخطوة الـ15 ليست تجميلاً:
            // العلامات تُوزَّع بتباعد متساوٍ على الشريط (flex-1)، والقيم القديمة
            // (960/980/1000/1015/1030/1045) خطواتها 20‏,20‏,15‏,15‏,15 — أي أن كل علامة
            // كانت تشير إلى لون ليس لونها. الخطوة الثابتة تُصلح ذلك بلا حيلة إضافية.
            gradient: gradientFor('pressure', 970, 1045),
            labels: units.pressure === 'hPa'
                ? ['970', '985', '1000', '1015', '1030', '1045']
                : ['28.6', '29.1', '29.5', '29.9', '30.4', '30.9'],
        },
        humidity: {
            title: t('legend.humidity'),
            gradient: gradientFor('humidity', 0, 100),
            labels: ['0', '25', '50', '75', '100'],
        },
        dewpoint: {
            title: t('legend.dewpoint', { unit: tempUnit }),
            gradient: gradientFor('dewpoint', -30, 30),
            labels: units.temperature === 'celsius'
                ? ['-30', '-20', '-10', '0', '10', '20', '30']
                : ['-22', '-4', '14', '32', '50', '68', '86'],
        },
        clouds: {
            title: t('legend.clouds'),
            gradient: gradientFor('clouds', 0, 100),
            labels: ['5', '25', '50', '75', '100'],
        },
        /* شذوذ 850 — مفتاح متباعد كمفتاح المحيطات، لكن حزمه منفصلة لا متدرّجة:
           الشريط نفسه يُظهر القفزات لأنه يُبنى من نقاط الألوان مباشرةً. */
        't850-anomaly': {
            title: t('legend.t850Anomaly', { unit: tempUnit }),
            gradient: gradientFor('t850-anomaly', -30, 30),
            labels: ['-30', '-24', '-18', '-12', '-6', '0', '+6', '+12', '+18', '+24', '+30'],
        },
        /*
         * **مفتاح متباعد**: الصفر في منتصف الشريط لا في طرفه، وهو الفرق الجوهري عن كل
         * ما فوقه. القيمة صفر هنا تعني «الوضع الطبيعي» لا «أدنى القيم»، والاتجاهان
         * (أدفأ/أبرد) متكافئان — فشريطٌ أحادي الاتجاه كان سيقرأ «−4» كأدنى القيم بدل
         * أقصى برودة شاذّة، أي يقلب المعنى لا يشوّهه فحسب.
         *
         * والمدى **±5 كامل مدى الترميز**، لا ±4.
         *
         * جُرّب ±4 بحجّة أن الطرف الأخير «نادر فيهدر ربع الشريط». والحجّة خاطئة رقمياً:
         * القصّ عند ±5 يمسّ ~5800 خليّة في كل إطار (شذوذ Niño 1+2 بلغ +4.0 فعلاً)،
         * وتلك الخلايا تُرسم على الخريطة بأغمق الأحمر وأغمق الأزرق. فكان على الخريطة
         * لونان لا يقابلهما شيء في المفتاح — ورصده المستخدم فوراً: «لا يوجد لون به 5
         * مع أنه يوجد قيم 5 على الخريطة».
         *
         * القاعدة التي انتُهك: **المفتاح يغطّي مدى بياناته كاملاً**. مفتاحٌ أضيق يكذب
         * على الخريطة التي يشرحها، وهو أسوأ من شريط مضغوط قليلاً.
         */
        /* النسبي: نفس السلّم ونفس الوحدة، والعنوان وحده يفرّق. عرضهما بعنوان واحد
           كان سيوهم أنهما رقم واحد — وهما ليسا كذلك (‏+2.60 مقابل +1.71 لنفس اليوم). */
        'sst-anomaly-rel': {
            title: t('legend.sstAnomalyRel', { unit: tempUnit }),
            gradient: gradientFor('sst-anomaly-rel', -5, 5),
            labels: ['-5', '-4', '-3', '-2', '-1', '0', '+1', '+2', '+3', '+4', '+5'],
        },
        'sst-anomaly': {
            title: t('legend.sstAnomaly', { unit: tempUnit }),
            gradient: gradientFor('sst-anomaly', -5, 5),
            labels: ['-5', '-4', '-3', '-2', '-1', '0', '+1', '+2', '+3', '+4', '+5'],
        },
        /* الرادار وحده يبني تدرّجه من `radarPalette.ts` لا من `getHeatmapColor`:
           مقياسه dBZ من رصدٍ لا من حقل نموذج، وسلالم الألوان في colorScales مفهرسة
           بأنواع النموذج. المصدر واحد على أي حال — نفس ملفّ JSON الذي يلوّن به
           المولّد النسيج، فيستحيل أن ينحرف المفتاح عن الخريطة. */
        radar: {
            title: t('legend.radar', { unit: RADAR_UNIT }),
            gradient: radarGradientCss(),
            labels: RADAR_LEGEND_LABELS.map(String),
        },
        // المتوقَّع بنفس مفتاح المرصود حرفياً: نفس الكمّية ونفس التدرّج (RADAR_SIM_STOPS).
        'radar-sim': {
            title: t('legend.radar-sim', { unit: RADAR_UNIT }),
            gradient: radarGradientCss(),
            labels: RADAR_LEGEND_LABELS.map(String),
        },
    };

    // حارس التطوير: يمنع انفصال `LEGEND_LAYER_IDS` عن `legends` بصمت. الانفصال لا
    // يُعطب المفتاح نفسه (هذا السطر يبقى صحيحاً) بل **موضع الشعار والمبدّل** فوقه.
    if (import.meta.env.DEV) {
        const built = Object.keys(legends);
        const mismatch = built.length !== LEGEND_LAYER_IDS.size
            || built.some((k) => !LEGEND_LAYER_IDS.has(k));
        if (mismatch) {
            console.error(
                'CentralLegend: LEGEND_LAYER_IDS لا تطابق legends — سيختلّ موضع الشعار/مبدّل النموذج على الجوّال.',
                { declared: [...LEGEND_LAYER_IDS], built },
            );
        }
    }

    const legend = legends[activeLayer];
    if (!legend) return null;

    const unitMatch = legend.title.match(/\(([^)]+)\)/);
    const unit = unitMatch ? unitMatch[1] : '';

    return (
        // ملاحظة: mb-2 على العنوان و justify-between على الأرقام كانا يأتيان من
        // 07-legend-bar.css (قواعد عامة لم يتجاوزها 26)، فأُدمجا هنا حرفياً.
        <div className="central-legend absolute bottom-4 right-4 z-[550] w-[240px] px-3 py-1.5 rounded-[10px] bg-[rgba(15,23,42,0.85)] light:bg-[var(--panel-bg)] backdrop-blur-[12px] border border-white/10 light:border-[var(--panel-border)] light:text-[var(--text-primary)] shadow-[0_6px_24px_rgba(0,0,0,0.5)] light:shadow-[var(--panel-shadow)] animate-fade-in [animation-duration:0.4s] pointer-events-auto max-[768px]:top-0 max-[768px]:inset-x-0 max-[768px]:bottom-auto max-[768px]:w-full max-[768px]:border-none max-[768px]:rounded-none max-[768px]:shadow-none max-[768px]:backdrop-blur-none max-[768px]:p-0 legend-hidden:max-[768px]:hidden">
            {/* أصناف legend-* دلالية: يعيد بها وضع اللوحي (27-responsive-mobile.css) استعمال
                شكل الجوّال — شريط لوني والأرقام فوقه — دون تكرار أدوات Tailwind هنا. */}
            <div className="legend-stack flex flex-col gap-1 max-[768px]:relative max-[768px]:block">
                <div className="legend-title text-[10px] font-bold text-white/70 text-center mb-2 max-[768px]:hidden">{legend.title}</div>
                <div className="legend-gradient h-1.5 w-full rounded-[3px] border border-white/10 max-[768px]:absolute max-[768px]:inset-0 max-[768px]:h-full max-[768px]:border-none max-[768px]:rounded-none max-[768px]:m-0" style={{ background: legend.gradient }} />
                <div className="legend-labels flex items-center justify-between text-[9px] font-semibold text-white opacity-100 [direction:ltr] max-[768px]:relative max-[768px]:z-[1] max-[768px]:px-2.5 max-[768px]:py-[5px] max-[768px]:text-[13px] max-[768px]:font-bold max-[768px]:m-0 max-[768px]:shadow-[0_0px_20px_rgb(0,0,0)]">
                    <div className="flex justify-between flex-1">
                        {legend.labels.map((label) => <span key={label} className="flex-1 text-center">{label}</span>)}
                    </div>
                    {unit && <span className="legend-unit hidden max-[768px]:inline-block max-[768px]:text-[11px] max-[768px]:font-semibold max-[768px]:opacity-100 max-[768px]:ms-1 max-[768px]:whitespace-nowrap">{unit}</span>}
                </div>
            </div>
        </div>
    );
});
